'use client'

import { Suspense, useActionState } from 'react'

import { useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { MRRCard } from '@/components/ui/mrr-card'
import { ContentContainer } from '@/components/ui/content-container'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { signIn } from './actions'
import { createClient } from '@/lib/supabase/client'

const initialState = { error: '' }

function SignInContent() {
  const searchParams = useSearchParams()
  const authError = searchParams.get('error')
  const redirectTo = searchParams.get('redirectTo')
  
  // Validate redirectTo to prevent open redirects
  const isValidRedirect = (url: string) => url.startsWith('/') && !url.startsWith('//')
  const validRedirectTo = redirectTo && isValidRedirect(redirectTo) ? redirectTo : null

  const [state, formAction, isPending] = useActionState(
    async (_prevState: typeof initialState, formData: FormData) => {
      // Pass the validated redirectTo to the server action
      if (validRedirectTo) {
        formData.set('redirectTo', validRedirectTo)
      }
      const result = await signIn(formData)
      return result || initialState
    },
    initialState
  )

  async function signInWithGoogle() {
    const supabase = createClient()
    // Build callback URL with redirect param if present
    const callbackUrl = new URL('/auth/callback', window.location.origin)
    if (validRedirectTo) {
      callbackUrl.searchParams.set('next', validRedirectTo)
    }
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: callbackUrl.toString(),
      },
    })
  }

  return (
    <div className="min-h-screen bg-paper flex flex-col safe-top safe-bottom">
      {/* Subtle noise overlay */}
      <div className="noise-overlay" aria-hidden="true" />

      <main className="flex-1 flex flex-col justify-center pb-16">
        <ContentContainer size="narrow">
          <MRRCard chapterLabel="SIGN IN">
            {/* Close button */}
            <Link 
              href="/" 
              className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center rounded-full bg-slate-faint hover:bg-ink/10 transition-colors group"
              aria-label="Close and return to home"
            >
              <svg className="w-5 h-5 text-ink-muted group-hover:text-ink transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Link>

            <h1 className="font-serif text-question text-ink">
              Welcome back
            </h1>
            <p className="text-body text-ink-muted mt-4">
              Sign in to continue where you left off.
            </p>

            {/* OAuth error message */}
            {authError === 'auth_callback_failed' && (
              <p className="text-body-sm text-plum mt-4">
                Google sign-in failed. Please try again.
              </p>
            )}
            {authError === 'already_in_other_couple' && (
              <p className="text-body-sm text-plum mt-4">
                This account is already connected to another couple. Sign out and create a new account to accept this invite.
              </p>
            )}

            {/* Google OAuth button */}
            <div className="mt-6">
              <button
                type="button"
                onClick={signInWithGoogle}
                className="flex w-full items-center justify-center gap-3 min-h-[54px] rounded-xl border border-slate-faint bg-white px-4 text-[15px] font-medium text-ink transition-all active:scale-[0.99] hover:bg-white/80"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-faint" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-paper-card px-4 text-ink-muted">or</span>
              </div>
            </div>

            <form action={formAction} className="space-y-4">
              <Input
                label="Email"
                type="email"
                name="email"
                autoComplete="email"
                required
                className="min-h-[52px] rounded-xl border-ink/15 bg-white text-[15px] placeholder:text-ink-faint focus:border-copper focus:ring-2 focus:ring-copper/15"
              />
              <div>
                <Input
                  label="Password"
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  className="min-h-[52px] rounded-xl border-ink/15 bg-white text-[15px] placeholder:text-ink-faint focus:border-copper focus:ring-2 focus:ring-copper/15"
                />
                <div className="mt-1.5 text-right">
                  <Link href="/forgot-password" className="text-[13px] text-copper hover:text-copper/80 transition-colors">
                    Forgot your password?
                  </Link>
                </div>
              </div>

              {state.error && (
                <p className="text-body-sm text-plum">{state.error}</p>
              )}

              <div className="pt-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isPending}
                  className="w-full"
                >
                  {isPending ? 'Signing in...' : 'Sign in →'}
                </Button>
              </div>
            </form>

            <p className="text-[14px] text-ink-muted mt-6 text-center">
              New here?{' '}
              <Link
                href={validRedirectTo ? `/sign-up?redirectTo=${encodeURIComponent(validRedirectTo)}` : '/sign-up'}
                className="font-semibold text-copper hover:underline"
              >
                Start one conversation free →
              </Link>
            </p>
          </MRRCard>
        </ContentContainer>
      </main>
    </div>
  )
}

export default function SignInClient() {
  return (
    <Suspense fallback={null}>
      <SignInContent />
    </Suspense>
  )
}
